import * as Sentry from "@sentry/gatsby";
import { QueueError, ResultError } from "@/utilities/message/message";
import type {
  VerificationErrorInfo,
  VerificationErrorSource,
} from "../types/VerificationError";

type BuildArgs = {
  source: VerificationErrorSource;
  cause: unknown;
  /** Override the user-facing message. Falls back to backend `displayText` or generic copy. */
  message?: string;
};

/**
 * Normalises any verification-flow failure into a `VerificationErrorInfo` and
 * captures the underlying cause in Sentry. Call once per failure at the hook
 * layer — do not capture again upstream.
 */
export const buildVerificationErrorInfo = ({
  source,
  cause,
  message,
}: BuildArgs): VerificationErrorInfo => {
  const isResponseError =
    cause instanceof ResultError || cause instanceof QueueError;

  const info: VerificationErrorInfo = {
    source,
    message:
      message ??
      (isResponseError && cause.message
        ? cause.message
        : "Something went wrong"),
    code: isResponseError ? cause.code : undefined,
    messageId: isResponseError ? cause.messageId : undefined,
    createdAt: isResponseError ? cause.createdAt : new Date(),
    validationErrors: isResponseError ? cause.errors : undefined,
    cause,
  };

  Sentry.captureException(cause, {
    tags: { feature: "verification", verificationErrorSource: source },
    extra: {
      code: info.code,
      messageId: info.messageId,
      validationErrors: info.validationErrors,
    },
  });

  return info;
};
